import { createProject, getProject, updateMedia, updateProject } from '@/infrastructure/storage'
import { mediaLibraryService } from '@/features/media-library/services/media-library-service'
import { clipFileUrl, getCatalogClip, getDraftProject } from './api'
import { collectFontSlugsFromProject, loadCustomFonts } from './fonts'
import { setIdMapEntry } from './idmap'
import { setLoadedVersion } from './version'
import { createLogger } from '@/shared/logging/logger'
import { makeFilename } from './filename'

const log = createLogger('smm-hydrate')

export interface HydrateResult {
  projectId: string
  clipCount: number
}

/** Load a draft's project + clips from the server into this device's OPFS so the
 *  editor route can open it. Idempotent: re-hydrating refreshes the project to the
 *  server's latest and reuses already-imported (content-hash-deduped) media. The
 *  draft id is reused as the freecut project id. */
export async function hydrateDraft(draftId: string, serverVersion?: number): Promise<HydrateResult> {
  const project = (await getDraftProject(draftId)) as {
    id?: string
    timeline?: { items?: Array<Record<string, unknown>> }
  }
  const items = project.timeline?.items ?? []
  // T29: pre-load any custom fonts referenced by this project. Best-effort.
  await loadCustomFonts(collectFontSlugsFromProject(project))
  const catalogIds = [
    ...new Set(
      items.filter((i) => i.type === 'video' && i.mediaId).map((i) => i.mediaId as string),
    ),
  ]

  const idMap: Record<string, string> = {}
  for (const catalogId of catalogIds) {
    if (idMap[catalogId]) continue
    // credentials: 'include' so the CF_Authorization cookie reaches smm-api.
    // colectiva.casa — the API hostname is Access-protected, and without the
    // cookie the request is redirected to the login page and CORS-blocked.
    const meta = await mediaLibraryService.importMediaFromUrl(
      clipFileUrl(catalogId),
      draftId,
      { credentials: 'include' },
    )
    idMap[catalogId] = meta.id
    setIdMapEntry(draftId, catalogId, meta.id) // remember for autosave's reverse-map

    // Enrich freecut MediaMetadata with catalog data so tags and description
    // appear in the media inspector. Fail-soft per spec §6 — a metadata fetch
    // failure must not block the project loading.
    try {
      const clip = await getCatalogClip(catalogId)
      await updateMedia(meta.id, {
        tags: clip.keywords,
        description: clip.description ?? undefined,
        fileName: makeFilename({ id: clip.id, category: clip.category }),
      })
    } catch (e) {
      log.warn('enrichment skipped for', catalogId, e)
    }
  }
  for (const it of items) {
    if (it.type === 'video' && it.mediaId && idMap[it.mediaId as string]) {
      it.mediaId = idMap[it.mediaId as string]
    }
  }

  project.id = draftId
  if (await getProject(draftId)) await updateProject(draftId, project as never)
  else await createProject(project as never)

  if (serverVersion !== undefined) setLoadedVersion(draftId, serverVersion)
  return { projectId: draftId, clipCount: catalogIds.length }
}
